<template>
  <div>
    <!--Info-->
    <div class="info-wrapper">
      <h3>{{ content.title }}</h3>
      <p>{{ content.description }}</p>
    </div>

    <!--Plan Durations-->
    <div v-if="hasPlanDurations" class="plan-duration-wrapper">
      <span
        class="duration-option"
        :class="{ selected: planDurationType === 'month' }"
        @click="changePlanDuration('month')"
      >
        Monthly
      </span>
      <span
        class="duration-option"
        :class="{ selected: planDurationType === 'year' }"
        @click="changePlanDuration('year')"
      >
        Annual
      </span>
    </div>

    <div class="plans-wrapper" :class="{ 'single-plan': plans.length === 1 }">
      <!--Plans-->
      <div class="plans">
        <div
          v-for="plan in plans"
          :key="plan.title"
          class="plan"
          :class="{ highlight: plan.isHighlighted }"
        >
          <!--Details-->
          <h3 class="title">{{ plan.title }}</h3>
          <p class="description">{{ plan.description }}</p>

          <!--Pricing-->
          <div class="price-details">
            <div class="plan-price">
              <span>${{ plan.pricing[planDurationType].amount }}</span>
              <em>/ {{ plan.pricing[planDurationType].unit }}</em>
              <em>/ {{ planDurationType }}</em>
            </div>
          </div>

          <!--Button-->
          <button
            class="btn btn-border btn-subscribe"
            @click="
              subscribePlan(plan.pricing[planDurationType].chargebeePlanId)
            "
          >
            {{ plan.buttonText }}
          </button>

          <!--Inclusions-->
          <div class="inclusions">
            <div
              v-for="inclusion in plan.inclusions"
              :key="inclusion.id"
              class="inclusion"
            >
              <unicon name="check"></unicon>
              <span>{{ inclusion }}</span>
            </div>
          </div>
        </div>
      </div>

      <!--FAQs-->
      <div class="faqs">
        <div v-for="faq in faqs" :key="faq.question" class="faq-item">
          <span class="question">{{ faq.question }}</span>
          <span class="answer">{{ faq.answer }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AvailableMeteredPlans",
  props: {
    content: Object,
    hasPlanDurations: { type: Boolean, default: false },
    plans: Array,
    faqs: Array,
  },
  data() {
    return {
      planDurationType: "month",
    };
  },
  computed: {},
  created() {},
  methods: {
    changePlanDuration(durationType) {
      this.planDurationType = durationType;
    },

    subscribePlan(planId) {
      this.$emit("subscribePlan", planId);
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/shared/dashboard.scss";

.info-wrapper {
  margin-bottom: 1rem;

  h3 {
    font-family: $titleFont;
    font-size: $mediumFontSize;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: $smallFontSize;
    opacity: $lightOpacity;
  }
}

.plans {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin: 0 0 3rem;
  gap: 1rem;
}

.plan {
  position: relative;
  flex: 1;
  padding: 1.5rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid #ececec;

  &.highlight {
    border: 2px solid $greenColor;
  }

  .title {
    color: $blackColor;
    font-weight: $boldFontWeight;
    font-size: $mediumFontSize;
    margin-bottom: 0;
  }

  .description {
    font-size: $smallerFontSize;
    opacity: 0.75;
    margin: 0.25rem 0 1rem;
    line-height: 1.4;
  }

  .plan-price {
    display: flex;
    flex-direction: row;
    align-items: flex-end;

    span {
      display: block;
      font-family: $titleFont;
      color: $blackColor;
      font-weight: $boldFontWeight;
      font-size: $largestFontSize;
      line-height: 1.5rem;
    }

    em {
      color: lighten($greyColor, 15%);
      font-weight: $mediumFontWeight;
      font-size: $normalFontSize;
      margin-left: 0.45rem;
      line-height: $normalFontSize;
    }
  }

  .btn-subscribe {
    font-size: $smallFontSize;
    display: block;
    width: 100%;
    background: $blackColor;
    border-color: lighten($blackColor, 15%);
    color: $whiteColor;
    margin: 1.5rem auto 0;

    &:hover {
      background: $purpleColor;
      border-color: lighten($purpleColor, 15%);
      color: $whiteColor;
    }

    &.btn-current-plan {
      width: calc(100% - 1rem);
      background: #f2fae9 !important;
      color: #5a876f !important;
      border-color: #adcebd !important;
    }
  }

  .inclusions {
    margin-top: 2rem;

    .inclusion {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-bottom: 0.25rem;

      :deep(svg) {
        background-color: $greenColor;
        fill: darken($greenColor, 40%);
        border-radius: 50%;
        width: auto;
        height: 0.85rem;
        padding: 0rem;
        margin-right: 0.75rem;
      }

      span {
        font-size: $smallFontSize;
        color: lighten($greyColor, 5%);
      }
    }
  }
}

.plans-wrapper {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-top: 2rem;

  &.single-plan {
    .plans {
      width: 30rem;
      margin-right: 5rem;
    }

    .faqs {
      width: calc(80% - 30rem);
      margin-top: 0.5rem;
    }
  }
}

.faqs {
  .faq-item {
    display: block;
    margin-bottom: 2.5rem;
  }

  .question {
    display: block;
    font-size: $smallFontSize;
    font-weight: $mediumFontWeight;
    margin-bottom: 0.5rem;
  }

  .answer {
    color: $greyColor;
    font-size: $smallerFontSize;
  }
}

.plan-duration-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  background: darken($whiteColor, 5%);
  border-radius: 0.5rem;
  padding: 0.25rem;
  gap: 0.25rem;
  margin: 2rem 0 -0.5rem;
  width: fit-content;

  .duration-option {
    font-size: $smallerFontSize;
    padding: 0.35rem 0.85rem;
    border-radius: 0.35rem;
    color: darken($whiteColor, 75%);
    cursor: pointer;

    &:hover {
      background: darken($whiteColor, 10%);
    }

    &.selected {
      background-color: $blackColor;
      color: $whiteColor;
    }
  }
}
</style>
